import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type { AccommodationDayInfo, AccommodationDayInfoResponse } from "@/types/api";

function isAvailableNight(info: AccommodationDayInfo | undefined): boolean {
  return !!info && info.open && info.has_price !== false && info.stock > 0;
}

export function getValidArrivalDays(
  dayList: AccommodationDayInfoResponse["day_list"],
  nights: number,
): Date[] {
  return Object.keys(dayList)
    .map((dateStr) => parseISO(dateStr))
    .filter((arrival) => isValidStay(dayList, arrival, nights));
}

export function computeStayLengthAvailability(
  dayList: AccommodationDayInfoResponse["day_list"],
): Record<number, boolean> {
  const result: Record<number, boolean> = {};
  for (let n = 1; n <= 30; n++) {
    result[n] = getValidArrivalDays(dayList, n).length > 0;
  }
  return result;
}

export function findClosestAvailableNights(
  availability: Record<number, boolean>,
  nights: number,
): number | null {
  if (availability[nights]) return null;
  for (let delta = 1; delta <= 29; delta++) {
    for (const candidate of [nights - delta, nights + delta]) {
      if (candidate >= 1 && candidate <= 30 && availability[candidate]) {
        return candidate;
      }
    }
  }
  return null;
}

function isValidStay(
  dayList: AccommodationDayInfoResponse["day_list"],
  arrivalDate: Date,
  nights: number,
): boolean {
  const arrivalInfo = dayList[format(arrivalDate, "yyyy-MM-dd")];
  if (!isAvailableNight(arrivalInfo) || !arrivalInfo?.can_arrive) return false;
  if (differenceInCalendarDays(arrivalDate, new Date()) < (arrivalInfo.lead_days ?? 0))
    return false;
  if (
    !Number.isInteger(nights) ||
    nights < Math.max(1, arrivalInfo.min_nights ?? 1) ||
    (arrivalInfo.max_nights !== null && nights > arrivalInfo.max_nights)
  )
    return false;

  for (let i = 1; i < nights; i++) {
    if (!isAvailableNight(dayList[format(addDays(arrivalDate, i), "yyyy-MM-dd")])) return false;
  }
  // Departure must be permitted and open, but does not need a nightly price.
  const departure = dayList[format(addDays(arrivalDate, nights), "yyyy-MM-dd")];
  return !!departure && departure.open && departure.stock > 0 && departure.can_departure;
}
